/**
 * Stripe Checkout Composable
 *
 * Provides functions for initiating Stripe Checkout and verifying payment status.
 * Uses full redirect to Stripe Checkout (not Elements) for reliable mobile support.
 */

/**
 * Pull a useful message out of an ofetch / $fetch error.
 * The server sets `message` via h3's createError(), which ofetch puts on
 * `err.data.message`. `err.message` itself is the unhelpful "[POST] /api/x: 4xx".
 */
function extractFetchErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const fetchErr = err as { data?: { message?: string, statusMessage?: string }, statusMessage?: string, message?: string }
    if (fetchErr.data?.message) return fetchErr.data.message
    if (fetchErr.data?.statusMessage) return fetchErr.data.statusMessage
    if (fetchErr.statusMessage) return fetchErr.statusMessage
    if (fetchErr.message) return fetchErr.message
  }
  return fallback
}

interface CheckoutOptions {
  quantity?: number
  childrenIds?: string[]
}

interface CheckoutResponse {
  url: string
  sessionId: string
  tierLabel: string
  unitAmount: number
}

interface SessionStatusResponse {
  status: 'pending' | 'completed' | 'expired'
  order?: {
    id: string
    total: number
    items: Array<{
      productName: string
      tierLabel: string
      quantity: number
      unitAmount: number
    }>
  }
  stripeStatus?: string
  paymentStatus?: string
  orderPending?: boolean
}

export const useStripeCheckout = () => {
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const route = useRoute()

  /**
   * Start a checkout session for a product
   * Redirects user to Stripe Checkout page
   */
  const startCheckout = async (
    productId: string,
    options?: CheckoutOptions,
  ): Promise<void> => {
    isLoading.value = true
    error.value = null

    try {
      const response = await $fetch<CheckoutResponse>('/api/stripe/create-checkout', {
        method: 'POST',
        body: {
          productId,
          quantity: options?.quantity ?? 1,
          childrenIds: options?.childrenIds,
          returnUrl: route.fullPath,
        },
      })

      // Full redirect to Stripe Checkout (no iframe)
      // This works reliably on all devices including mobile
      if (response.url) {
        // Still loading while the browser leaves, so a second tap can't
        // start a second checkout
        window.location.href = response.url
      }
      else {
        throw new Error('No checkout URL returned')
      }
    }
    catch (err) {
      const message = extractFetchErrorMessage(err, 'Failed to start checkout')
      error.value = message
      isLoading.value = false
      throw err
    }
  }

  /**
   * Check the status of a checkout session
   * Used after redirect back from Stripe
   */
  const checkSessionStatus = async (
    sessionId: string,
  ): Promise<SessionStatusResponse> => {
    isLoading.value = true
    error.value = null

    try {
      const response = await $fetch<SessionStatusResponse>(
        `/api/stripe/session-status?session_id=${sessionId}`,
      )
      return response
    }
    catch (err) {
      const message = extractFetchErrorMessage(err, 'Failed to check session status')
      error.value = message
      throw err
    }
    finally {
      isLoading.value = false
    }
  }

  /**
   * Format a price in cents to a display string
   */
  const formatPrice = (cents: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100)
  }

  return {
    isLoading: readonly(isLoading),
    error: readonly(error),
    startCheckout,
    checkSessionStatus,
    formatPrice,
  }
}
